import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Icon from "../Icon";
import {
  faBars,
  faCircleQuestion,
  faHouse,
  faPhone,
  faTooth,
  faUserDoctor,
} from "@fortawesome/free-solid-svg-icons";

// Matches Semantic UI's sidebar transition (semantic-subset.css)
const SIDEBAR_ANIMATION_MS = 500;

const HeaderMenu = () => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [activeItem, setActiveItem] = useState("HOME");
  const sidebarRef = useRef(null);
  const toggleRef = useRef(null);

  const setSidebarVisible = useCallback((open) => {
    setVisible(open);
    // Keeps the sidebar visible while it slides in or out
    setAnimating(true);
  }, []);

  useEffect(() => {
    if (!animating) return;
    const timer = setTimeout(() => setAnimating(false), SIDEBAR_ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [animating, visible]);

  // Close on a click outside the sidebar or on Escape
  useEffect(() => {
    if (!visible) return;
    const onClick = (event) => {
      if (
        !sidebarRef.current.contains(event.target) &&
        !toggleRef.current.contains(event.target)
      ) {
        setSidebarVisible(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setSidebarVisible(false);
    };
    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [visible, setSidebarVisible]);

  const handleItemClick = (name, path) => {
    setActiveItem(name);
    navigate(path);
    setSidebarVisible(false);
  };

  const toggleVisibility = () => setSidebarVisible(!visible);

  const getMenuItems = () => {
    const generateItem = (name, path, icon, ariaLabel) => {
      return (
        <span
          key={name}
          className="menu-item"
          onClick={() => handleItemClick(name, path)}
          role="none"
        >
          <Link
            className={activeItem === name ? "active item" : "item"}
            to={path}
            aria-label={ariaLabel}
            aria-current={activeItem === name ? "page" : undefined}
          >
            {name}
          </Link>
          <Icon icon={icon} />
        </span>
      );
    };
    return (
      <>
        {generateItem("HOME", "/", faHouse, "Go to homepage")}
        {generateItem("SERVICES", "/services", faTooth, "View our dental services")}
        {generateItem("FAQ", "/faq", faCircleQuestion, "Frequently asked questions")}
        {generateItem("ABOUT", "/about", faUserDoctor, "Learn about our team")}
        {generateItem("CONTACT", "/contact", faPhone, "Contact us")}
      </>
    );
  };

  // Same markup and class names Semantic UI's Sidebar/Menu rendered, styled
  // by semantic-subset.css
  const sidebarClassName = [
    "ui labeled icon overlay right thin sidebar sidebar-menu menu",
    visible && "visible",
    animating && "animating",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div>
      <button
        ref={toggleRef}
        className="hamburger-menu-icon"
        onClick={toggleVisibility}
        aria-label={visible ? "Close navigation menu" : "Open navigation menu"}
        aria-expanded={visible}
        aria-controls="mobile-menu"
        type="button"
      >
        <Icon icon={faBars} className="hamburger-icon" />
      </button>
      <div
        ref={sidebarRef}
        id="mobile-menu"
        className={sidebarClassName}
        aria-label="Mobile navigation menu"
      >
        <div className="ui huge text vertical menu">{getMenuItems()}</div>
      </div>
      <div className="full-menu">
        <nav aria-label="Main navigation">
          <div className="ui large text menu">{getMenuItems()}</div>
        </nav>
      </div>
    </div>
  );
};

export default HeaderMenu;
